import { Injectable } from '@angular/core';

import * as _ from 'lodash';

import { StateService } from '@uirouter/core';
import { Schedule } from 'src/app/api/services/schedule';
import { CompanyStateService } from 'src/app/auth/services/company-state.service';
import { PermissionService } from 'src/app/auth/services/permission.service';
import { SCHEDULE_ACCESS } from 'src/app/auth/types/access-level';
import { ActionsMenu } from 'src/app/components/common-actions/common-actions.component';
import { TrackerService } from 'src/app/components/logging/tracker.service';
import { ModalService } from 'src/app/components/modals/modal.service';
import { FeaturesService } from 'src/app/components/plans/features.service';
import { BaseEditableComponent } from 'src/app/shared/components/base-editable/base-editable.component';
import {
  AccessControlList,
  ShareWithUsersModalComponent,
} from 'src/app/components/share-with-users-modal/share-with-users-modal.component';
import { AssignUsersModalComponent } from '../components/assign-users-modal/assign-users-modal.component';
import { ScheduleService } from './schedule.service';

@Injectable({
  providedIn: 'root',
})
export class ScheduleActionsService {
  constructor(
    private modalService: ModalService,
    private featuresService: FeaturesService,
    private scheduleService: ScheduleService,
    private trackerService: TrackerService,
    private permissionService: PermissionService,
    private companyStateService: CompanyStateService,
    private stateService: StateService,
  ) {}

  getActions(component: BaseEditableComponent<any>): ActionsMenu {
    return {
      cancelAction: {
        id: 'cancelButton',
        label: 'Cancel',
        action: () => {
          component.bypassUnsavedChanges();
          this.stateService.go('apps.schedules.list');
        },
      },
      moreOptions: [
        {
          id: 'assignUsersButton',
          label: 'Assign Users',
          icon: 'add-user',
          disabled: !this.assignUsersVisible(),
          action: () => this.assignUsers(),
        },
        {
          id: 'shareScheduleButton',
          label: 'Share with others',
          icon: 'add-user',
          action: () => this.shareSchedule(),
          requireAccess: SCHEDULE_ACCESS.FULL_ACCESS,
          entityId: this.scheduleService.schedule?.id,
          requireRole: this.permissionService.isSaProductionReleaseEnabled()
            ? undefined
            : 'sa',
        },
      ],
    };
  }

  assignUsersVisible(): boolean {
    return (
      this.permissionService.hasAccess(SCHEDULE_ACCESS.FULL_ACCESS) &&
      (this.companyStateService.isK12Customer() ||
        this.companyStateService.isHigherEdCustomer())
    );
  }

  assignUsersToSchedule(selectedUsers: string[]) {
    let isFeatureAvailable = this.featuresService.isFeatureAvailable(
      FeaturesService.FEATURE_TEACHER_ROLE,
    );

    if (!isFeatureAvailable) {
      this.featuresService.showUpgradePlanModal(
        FeaturesService.FEATURE_TEACHER_ROLE,
      );

      return Promise.reject();
    }

    return this.modalService.showMediumDialog(
      AssignUsersModalComponent,
      {
        selectedUsers,
      },
      { height: '95%' },
    );
  }

  assignUsers() {
    const schedule = this.scheduleService.schedule;
    if (!schedule) {
      return;
    }

    this.assignUsersToSchedule(
      schedule.assignedUsers ? _.cloneDeep(schedule.assignedUsers) : [],
    )
      .then((users) => {
        if (!_.isEqual(schedule.assignedUsers, users)) {
          schedule.assignedUsers = users;
          this.trackerService.scheduleEvent(
            'Schedule Assigned Users Changed',
            schedule.scheduleId,
            schedule.scheduleName,
            {
              assignedUsers: users.length,
            },
          );
        }
      })
      .catch(() => {});
  }

  showShareDialog(accessControlList: Promise<AccessControlList>) {
    return this.modalService.showMediumDialog(
      ShareWithUsersModalComponent,
      {
        accessControlList,
        accessLevels: ['Playlist Manager', 'Full Access'],
        defaultAccessLevel: 'Playlist Manager',
        saveAccessControlList: async (
          updatedAccessControlList: AccessControlList,
          comment?: string,
        ) => {
          if (_.isEqual(await accessControlList, updatedAccessControlList)) {
            return;
          }

          const response = await this.scheduleService.updateAccessControlList(
            updatedAccessControlList as NonNullable<
              Schedule['accessControlList']
            >,
            comment,
          );
          this.scheduleService.schedule.accessControlList =
            response.item?.accessControlList ?? updatedAccessControlList;
          this.trackerService.scheduleEvent(
            'Schedule Access Changed',
            this.scheduleService.schedule.scheduleId,
            this.scheduleService.schedule.scheduleName,
            {
              sharedUsers: Object.values(updatedAccessControlList).filter(
                Boolean,
              ).length,
            },
          );
        },
      },
      { width: '640px', maxWidth: 'calc(100vw - 32px)' },
    );
  }

  shareSchedule(): void {
    const currentAccessControlList =
      this.scheduleService.getAccessControlList();

    this.showShareDialog(currentAccessControlList).catch(() => {});
  }
}
